import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './App.scss';
import * as Storage from './service/storage';

declare global {
  interface Window {
    _paq?: any[];
  }
}

function App() {
  const [username, setUsername] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);
  const [tokenChecked, setTokenChecked] = useState<boolean>(false);

  const matomoLoaded = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch username and email from JWT token
  useEffect(() => {
    const extractUserFromToken = async () => {
      try {
        const token = await Storage.TOKEN.get();
        if (!token || token.length <= 0) {
          navigate('login');
          return;
        }

        const decoded: any = require('jwt-decode')(token);
        // console.log('Decoded JWT:', decoded);

        if (decoded?.username) setUsername(decoded.username);
        else if (decoded?.name) setUsername(decoded.name);
        
        if (decoded?.email) setEmail(decoded.email);

        setTokenChecked(true);
      } catch (err) {
        console.error('Error decoding token:', err);
        navigate('login');
      }
    };

    extractUserFromToken();
  }, [navigate]);

  // Update screen width on resize (used by Profile)
  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Matomo Tracking Init
  useEffect(() => {
    if (!username || !tokenChecked) return;

    let matomoScript: HTMLScriptElement | null = null;

    fetch('/matomo_site.json')
      .then(res => {
        if (!res.ok) throw new Error('Failed to load matomo_site.json');
        return res.json();
      })
      .then(config => {
        window._paq = window._paq || [];
        window._paq.push(['setTrackerUrl', 'https://analytics.bywaters.co.uk/matomo.php']);
        window._paq.push(['setSiteId', config.siteId]);
        window._paq.push(['enableLinkTracking']);
        window._paq.push(['setUserId', username]);

        matomoScript = document.createElement('script');
        matomoScript.src = 'https://analytics.bywaters.co.uk/matomo.js';
        matomoScript.async = true;
        matomoScript.onload = () => {
          matomoLoaded.current = true;
          const fullUrl = window.location.pathname + window.location.search + window.location.hash;
          window._paq?.push(['setCustomUrl', fullUrl]);
          window._paq?.push(['setDocumentTitle', document.title]);
          window._paq?.push(['setUserId', username]);
          window._paq?.push(['trackPageView']);
        };
        document.body.appendChild(matomoScript);
      })
      .catch(err => {
        console.error('Matomo config fetch failed:', err);
      });

    return () => {
      if (matomoScript) {
        document.body.removeChild(matomoScript);
      }
    };
  }, [username, tokenChecked]);

  // Track SPA route changes
  useEffect(() => {
    if (matomoLoaded.current && window._paq && username) {
      const fullUrl = location.pathname + location.search + location.hash;
      window._paq.push(['setUserId', username]);
      window._paq.push(['setCustomUrl', fullUrl]);
      window._paq.push(['setDocumentTitle', document.title]);
      window._paq.push(['trackPageView']);
    }
  }, [location, username]);

  // Auth check + redirects (already validated in token extraction)
  useEffect(() => {
    const timer = setTimeout(() => {
      const currentPath = window.location.pathname;

      if (currentPath !== '/reset-password' && currentPath !== '/account-confirmation') {
        Storage.TOKEN.get().then(jwt => {
          if (!jwt || jwt.length <= 0) navigate('login');
        }).catch(err => {
          console.error(err);
          navigate('login');
        });
      } else {
        const urlParams = new URLSearchParams(window.location.search);
        navigate(`${currentPath}?${urlParams.toString()}`);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [navigate]);

  if (!username || !tokenChecked) {
    return (
      <div className="App">
        <img
          className="loading-logo"
          src="/images/bywaters_white_logo.png"
          alt="bywaters_logo"
        />
      </div>
    );
  }

  return (
    <div className="App">
      <img
        className="loading-logo"
        src="/images/bywaters_white_logo.png"
        alt="bywaters_logo"
      />
      {/* Optionally use username/email in the UI */}
    </div>
  );
}

export default App;
